<script setup lang="ts">
import { PropertyId } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendSearchAutocompleteRequest } from "~/classes/handlers/search/autocomplete"

defineProps({
  onlyIn: { type: Boolean }
})
const emit = defineEmits(["remove", "replace"])
const { state: instantState } = useInstantMenu()
const { state: sharedFolderState } = useSharedFolders()
const { addInterfaceEventListener, removeInterfaceEventListener } = useEvents()
const { state: myself } = useSelf()
const referencedFile = defineModel<string>("in")
const query = ref("")
const requestIds = new Set<string>()
const items = ref<StaticFile[]>([]) as Ref<StaticFile[]>

const quitAndRemove = () => {
  instantState.value.folderInSelectionMenuOpen = false
  instantState.value.folderInSelection = undefined
  emit("remove")
}

const quit = () => {
  instantState.value.folderInSelectionMenuOpen = false
}

const selectAndClose = (item: StaticFile) => {
  instantState.value.folderInSelection = item
  referencedFile.value = item.id
  instantState.value.folderInSelectionMenuOpen = false
}

const onAutocompleteResponse = (response: SearchAutocompleteResponseDatagram) => {
  if (!requestIds.has(response.requestId))
    return console.warn("Received response for unknown request", response.requestId)

  if (response.error || !response.files)
    return console.warn("Response errored", response)

  items.value = response.files.map(f => new StaticFile(f))
}

const onSwitchOnlyIn = (onlyIn: boolean, toggle: () => void) => {
  if (onlyIn)
    emit("replace", { propertyId: PropertyId.FILE_PARENT_ID, facetType: { fileOnlyIn: referencedFile.value ?? "" } })
  else
    emit("replace", { propertyId: PropertyId.FILE_VIRTUAL_PATH, facetType: { fileIn: referencedFile.value ?? "" } })
  toggle()
}

/**
 * TODO: implement throttling so that we only make occasional server requests
 */
watch(query, (query: string) => {
  if (!myself.value.home || !myself.value.archive) {
    console.error("Instant menu not supported in anon mode")
    return
  }
  query = query.trim()
  if (!query.length) {
    console.warn("Empty query for autocomplete request")
    return
  }

  const locations = [myself.value.home.id, myself.value.archive.id]
  for (const { home, archive } of Object.values(sharedFolderState.value.all))
    locations.push(home.id, archive.id)

  // short queries only have the starts-with and longer queries use all facets
  // and we only want non-folder files (TODO: other non-similarity-searchable file types)
  const facets: SearchFacet[] = [
    { propertyId: PropertyId.FILE_NAME, facetType: { startsWith: query } },
    { propertyId: PropertyId.FILE_MIME_TYPE, facetType: { equals: "inode/directory" } },
  ]
  if (query.length > 2)
    facets.push({ propertyId: PropertyId.FILE_NAME, facetType: { contains: query } })

  const requestId = generateWorkerRequestId()
  const request: SearchAutocompleteRequestDatagram = {
    requestId,
    offline: true, // TODO: fix
    fileSelection: InterfaceSelection.minimumAutocompleteSelection().inner(),
    pathSelection: InterfaceSelection.minimumPathSelection().inner(),
    limit: 10,
    locations,
    definition: { facets, sorts: [] },
  }

  requestIds.add(request.requestId)
  sendSearchAutocompleteRequest(request)
})

/**
 * If the user clicks out of the dropdown without selecting a file, remove the
 * whole facet
 */
watch(() => instantState.value.folderInSelectionMenuOpen, (active?: boolean) => {
  if (!active && !instantState.value.folderInSelection)
    return quitAndRemove()
})

onMounted(async () => {
  addInterfaceEventListener("searchAutocompleteResponse", onAutocompleteResponse)
})

onUnmounted(() => {
  removeInterfaceEventListener("searchAutocompleteResponse", onAutocompleteResponse)
  requestIds.clear()
})
</script>

<template>
  <dropdown
    v-model:active="instantState.folderInSelectionMenuOpen"
    anchor-x="left"
    anchor-y="bottom"
    flow-x="right"
    flow-y="down"
    :open-on-mount="!instantState.folderInSelection"
  >
    <template #default="{ open }">
      <div class="file-picker-container">
        <dropdown
          anchor-x="left"
          anchor-y="bottom"
          flow-x="right"
          flow-y="down"
          class="h-full"
        >
          <template #default="{ toggle }">
            <div
              class="file-picker-description"
              @click="toggle()"
            >
              {{ onlyIn ? 'Only In' : 'In' }}
            </div>
          </template>
          <template #menu="{ toggle }">
            <dropdown-menu class="mt-2">
              <div class="inline-select-dropdown">
                <div
                  class="inline-select-option t-menu-sm"
                  @click="onSwitchOnlyIn(false, toggle)"
                >
                  <div>In folder and subfolders</div>
                  <icon-action
                    v-if="!onlyIn"
                    size="xs"
                    type="check"
                    class="icon"
                  />
                  <div v-else class="icon" />
                </div>
                <div
                  class="inline-select-option t-menu-sm"
                  @click="onSwitchOnlyIn(true, toggle)"
                >
                  <div>Only in folder</div>
                  <icon-action
                    v-if="onlyIn"
                    size="xs"
                    type="check"
                    class="icon"
                  />
                  <div v-else class="icon" />
                </div>
              </div>
            </dropdown-menu>
          </template>
        </dropdown>
        <div class="file-picker-outer" @click="open()">
          <file-inline
            v-if="instantState.folderInSelection"
            :file="instantState.folderInSelection"
            class="file-picker-selection"
          />
        </div>
        <div class="file-picker-close" @click="quitAndRemove()">
          <icon-action type="cross" size="2xs" />
        </div>
      </div>
    </template>
    <template #menu>
      <nav-instant-facet-select
        v-model:query="query"
        :items
        placeholder="Search for a file..."
        class="file-picker-select-container"
        @select="item => selectAndClose(item)"
        @cancel="quit()"
      >
        <template #option="{ item, selected }">
          <div class="file-picker-option-wrapper" :class="{ selected }">
            <file-inline :file="item" />
          </div>
        </template>
      </nav-instant-facet-select>
    </template>
  </dropdown>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.file-picker-container
  @apply flex flex-none items-center gap-[#{$menu-search-gap-item-facet}] relative
  @apply h-[24px]
  @apply rounded-[#{$menu-br-item}] overflow-hidden
  @apply t-menu-sm

.file-picker-description
  @apply flex flex-none items-center
  @apply h-[24px] px-1.5
  @apply bg-$global-violet-accent
  @apply text-$global-violet-primary
  @apply cursor-pointer
  &:hover
    @apply bg-$global-violet-secondary

.file-picker-close
  @apply flex flex-none items-center
  @apply h-full px-1
  @apply bg-$global-violet-accent
  @apply text-$global-violet-primary
  @apply cursor-pointer
  &:hover
    @apply bg-$global-violet-secondary

.file-picker-outer
  @apply flex-none flex items-center gap-[#{$menu-search-gap-item-facet-section}]
  @apply h-full pl-1
  @apply bg-$global-violet-accent
  @apply text-$global-violet-primary
  @apply cursor-pointer
  &:hover
    @apply bg-$global-violet-secondary

.file-picker-file-icon
  @apply text-$global-violet-primary

.file-picker-selection
  @apply w-full

.file-picker-select-container
  @apply mt-2

.file-picker-option-wrapper
  &.selected, &:hover
    @apply bg-[#{$menu-bg-item-hover}] -m-1 p-1 rounded-[#{$menu-br-item}]

.inline-select-dropdown
  @apply flex flex-col gap-[#{$menu-gap-items}] px-[#{$menu-px-default}] py-[#{$menu-py-default}]
  @apply overflow-auto
  max-height: calc(100vh - 60px)

.inline-select-option
  @apply text-[#{$global-t-default}]
  @apply flex cursor-pointer items-center h-5 -m-1 p-1 gap-2
  @apply rounded-[#{$menu-br-item}]
  .icon
    @apply ml-auto h-4 w-4 text-[#{$global-bg-icon-secondary}]
  &.disabled
    @apply cursor-not-allowed opacity-50
  &:not(.disabled):hover
    @apply bg-[#{$menu-bg-item-hover}]
</style>
